import { useTranslation } from 'react-i18next';
import { ActionIcon, Box, Group, Stack, Text, ThemeIcon, Tooltip } from '@mantine/core';
import { IconCircleCheck, IconDownload, IconReceipt } from '@tabler/icons-react';
import SignatureBadge from '@/components/app/deals/SignatureBadge';
import NoNds from '@/components/common/NoNds';
import { useDownloadDocument } from '@/hooks/useDeals';
import { buildInvoices, isInvoiceSigned } from '@/lib/dealInvoices';
import { formatMoney } from '@/lib/format';
import type { BargainStepsInfo, SignatureRole } from '@/types/deals';

// Переменная (не строковый литерал в JSX) — иначе jsx-a11y/aria-role примет role="AGENT" за ARIA-роль.
const AGENT_ROLE: SignatureRole = 'AGENT';

interface InvoicesStepProps {
  readonly info: BargainStepsInfo;
}

// Шаг «Счета за товар и логистику» (id 2). Счета генерит 1С, подписывает АГЕНТ в ЭДО (Диадок);
// продавец/покупатель наблюдают. Ручной загрузки нет (only ЭДО). Read-only + статус подписи.
export default function InvoicesStep({ info }: InvoicesStepProps) {
  const { t } = useTranslation();
  const isAgent = info.userRole === 'AGENT';
  const download = useDownloadDocument();

  // Продавцу счета не показываем (бизнес-правило): счёт за товар выставлен покупателю, продавец ждёт оплаты.
  if (info.isSeller === true) {
    return (
      <Text fz="sm" c="gray.8">
        {t('app.deals.invoices.sellerWaiting')}
      </Text>
    );
  }

  // Суммы без НДС (сам НДС — внутри документа счёта); состав см. dealInvoices.buildInvoices.
  const invoices = buildInvoices(info);
  const allSigned = invoices.every((inv) => isInvoiceSigned(inv.doc));

  return (
    <Stack gap="md">
      {allSigned ? (
        <Group gap="xs" wrap="nowrap">
          <ThemeIcon size={32} radius="xl" variant="light" color="lime">
            <IconCircleCheck size={18} />
          </ThemeIcon>
          <Text fz="sm" fw={500}>
            {t('app.deals.invoices.signed')}
          </Text>
        </Group>
      ) : (
        <Text fz="sm" c="gray.8">
          {t(isAgent ? 'app.deals.invoices.agentSign' : 'app.deals.invoices.waiting')}
        </Text>
      )}

      <Stack gap="xs">
        {invoices.map((inv) => (
          <Group key={inv.key} justify="space-between" wrap="nowrap" align="flex-start" gap="sm">
            <Group gap="sm" wrap="nowrap" align="flex-start" style={{ minWidth: 0 }}>
              <ThemeIcon size={32} radius="md" variant="light" color="gray">
                <IconReceipt size={18} />
              </ThemeIcon>
              <Box style={{ minWidth: 0 }}>
                <Text fz="sm" fw={500}>
                  {t(inv.titleKey)}
                </Text>
                {inv.amount != null ? (
                  <Text fz="sm" c="gray.7">
                    {formatMoney(inv.amount)} ₽
                    <NoNds />
                  </Text>
                ) : null}
                <Box mt={4}>
                  {inv.doc == null ? (
                    <Text fz="xs" c="gray.6">
                      {t('app.deals.invoices.generating')}
                    </Text>
                  ) : inv.doc.signatures?.AGENT ? (
                    <SignatureBadge role={AGENT_ROLE} info={inv.doc.signatures.AGENT} />
                  ) : (
                    <Text fz="xs" c="gray.6">
                      {t('app.deals.invoices.waitShort')}
                    </Text>
                  )}
                </Box>
              </Box>
            </Group>
            {inv.doc?.id != null ? (
              <Tooltip label={t('app.deals.docs.download')}>
                <ActionIcon
                  variant="subtle"
                  color="gray"
                  aria-label={t('app.deals.docs.download')}
                  loading={download.isPending && download.variables === inv.doc.id}
                  onClick={() => download.mutate(inv.doc!.id!)}
                >
                  <IconDownload size={18} />
                </ActionIcon>
              </Tooltip>
            ) : null}
          </Group>
        ))}
      </Stack>
    </Stack>
  );
}
